
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';

interface AnimatedCardProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  animation?: 'fade-up' | 'scale-up' | 'slide-in' | 'fade-in';
  delay?: number;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  children?: React.ReactNode;
}

export const AnimatedCard = ({
  title,
  description,
  footer,
  animation = 'fade-up',
  delay = 0,
  className,
  contentClassName,
  headerClassName,
  footerClassName,
  children,
  ...props
}: AnimatedCardProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const animationClass = isVisible ? `animate-${animation}` : 'opacity-0';
  const delayStyle = { animationDelay: `${delay}ms` };

  return (
    <Card 
      className={cn(
        'transition-all duration-300 overflow-hidden',
        animationClass,
        className
      )} 
      style={delayStyle}
      {...props}
    >
      {(title || description) && (
        <CardHeader className={cn('p-6', headerClassName)}>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      {children && (
        <CardContent className={cn('p-6 pt-0', contentClassName)}>
          {children}
        </CardContent>
      )}
      {footer && (
        <CardFooter className={cn('p-6 pt-0 flex justify-between items-center', footerClassName)}>
          {footer}
        </CardFooter>
      )}
    </Card>
  );
};

export default AnimatedCard;
